import React from "react";

const SheetFiveLoader = ({ rows = 5, columns = 6, showSalesColumn = false, brandTypeCount = 3 }) => {
  const getColumnCount = () => {
    let baseColumns = 4; // Brand, Category, Total + months
    if (showSalesColumn) baseColumns += 1; // Sales Person column
    return baseColumns + columns - 1; // Adjust for months
  };

  const renderSkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="border p-2">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </td>
      <td className="border p-2">
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </td>
      {showSalesColumn && (
        <td className="border p-2">
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </td>
      )}
      {Array.from({ length: columns - 2 }).map((_, i) => (
        <td key={i} className="border p-2 text-center">
          <div className="h-3 bg-gray-200 rounded w-8 mx-auto"></div>
        </td>
      ))}
      <td className="border p-2 text-right">
        <div className="h-4 bg-gray-200 rounded w-16 inline-block"></div>
      </td>
    </tr>
  );

  const renderBrandTypeSkeleton = () => (
    <div className="flex gap-6 items-start mb-6">
      {/* Brand Type Label Skeleton */}
      <div className="bg-gray-200 animate-pulse p-1 w-32 h-8 rounded-md shadow flex items-center justify-center">
        <div className="h-4 bg-gray-300 rounded w-20"></div>
      </div>
      
      {/* Table Skeleton */}
      <div className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                {['Brand', 'Category', ...(showSalesColumn ? ['Sales Person'] : []), ...Array(columns - 3).fill('Month'), 'Total'].map((header, i) => (
                  <th key={i} className="border p-3">
                    <div className="h-4 bg-gray-300 rounded w-16 mx-auto"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, i) => (
                <React.Fragment key={i}>{renderSkeletonRow()}</React.Fragment>
              ))}
              {/* Grand Total Row Skeleton */}
              <tr className="bg-gray-50">
                <td colSpan={showSalesColumn ? 3 : 2} className="border p-2">
                  <div className="h-5 bg-gray-300 rounded w-24"></div>
                </td>
                {Array.from({ length: columns - 2 }).map((_, i) => (
                  <td key={i} className="border p-2 text-center">
                    <div className="h-4 bg-gray-300 rounded w-12 mx-auto"></div>
                  </td>
                ))}
                <td className="border p-2 text-right">
                  <div className="h-5 bg-gray-300 rounded w-20 inline-block"></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      {/* Header Controls Skeleton */}
      <div className="flex flex-wrap gap-6 items-end bg-white rounded px-4 py-6 mb-6 animate-pulse">
        <div className="flex flex-col space-y-2">
          <div className="h-4 bg-gray-300 rounded w-24"></div>
          <div className="h-10 bg-gray-200 rounded border w-48"></div>
        </div>
        <div className="flex flex-col space-y-2 min-w-[180px]">
          <div className="h-4 bg-gray-300 rounded w-20"></div>
          <div className="h-10 bg-gray-200 rounded border w-full"></div>
        </div>
        <div className="flex flex-col space-y-2 min-w-[220px]">
          <div className="h-4 bg-gray-300 rounded w-28"></div>
          <div className="h-10 bg-gray-200 rounded border w-full"></div>
        </div>
        <div className="flex flex-col space-y-2">
          <div className="h-4 bg-gray-300 rounded w-24"></div>
          <div className="w-16 h-6 bg-gray-200 rounded-full flex items-center p-1">
            <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
          </div>
        </div>
        <div className="h-10 bg-gray-300 rounded px-8 w-32 flex items-center justify-center"></div>
      </div>

      {/* Brand Type Skeletons */}
      {Array.from({ length: brandTypeCount }).map((_, i) => (
        <React.Fragment key={i}>{renderBrandTypeSkeleton()}</React.Fragment>
      ))}

      {/* Final Grand Total Skeleton */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                {Array.from({ length: getColumnCount() }).map((_, i) => (
                  <th key={i} className="border p-3">
                    <div className="h-4 bg-gray-300 rounded w-16 mx-auto"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-50 animate-pulse">
                <td colSpan={showSalesColumn ? 3 : 2} className="border p-3">
                  <div className="h-6 bg-gray-300 rounded w-32 font-semibold"></div>
                </td>
                {Array.from({ length: columns - 2 }).map((_, i) => (
                  <td key={i} className="border p-3 text-center">
                    <div className="h-5 bg-yellow-200 rounded w-16 mx-auto"></div>
                  </td>
                ))}
                <td className="border p-3 text-right">
                  <div className="h-6 bg-yellow-200 rounded w-24 inline-block font-semibold"></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SheetFiveLoader;